<script lang="ts">
	import type { PageData } from './$types';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import LL from '$i18n/i18n-svelte';
	import ActiveFilter from '$lib/components/ActiveFilter.svelte';
	import AnnouncementCard from '$lib/components/cards/NewsCard.svelte';
	import clsx from 'clsx';
	import { onMount, tick } from 'svelte';
	import { browser } from '$app/environment';
	import Select from '$lib/components/form/Select.svelte';
	import Button from '$lib/components/Button.svelte';
	import MultiSelect from '$lib/components/form/MultiSelect.svelte';

	export let data: PageData;
	$: ({ activities, filters } = data);

	let activeFilters = [];
	let Isotope;
	let imagesLoaded;
	let activitiesGroupedByYear = [];

	// Filter out dates via search params and group announcements by year
	$: filteredActivities =
		activeFilters.length > 0
			? Object.entries(filters)
					.reduce<any>((filteredActivities, [key, filter]) => {
						// Get active filters for this filter
						const active = activeFilters.filter((activeFilter) => activeFilter.key === key);

						if (active.length === 0) {
							return filteredActivities;
						}

						return active.flatMap(({ value }) => {
							return filteredActivities.filter((item) => filter.callback(item, value));
						});
					}, activities.items)
					.filter((activity) =>
						activeFilters.some((filter) => filter.key === 'subtype')
							? activity.categories?.some(
									(category) =>
										category.id === activeFilters.find((filter) => filter.key === 'subtype').id,
							  )
							: true,
					)
					.sort((a, b) => b.publishedAt - a.publishedAt)
			: activities.items;

	$: activitiesGroupedByType = filters.type.items
		.map((type) => ({
			value: type.value,
			label: type.label,
			items: filteredActivities
				.filter((item) =>
					item.categories.some((category) => type.items.some((subtype) => subtype.id === category.id)),
				)
				.sort((a, b) => b.publishedAt - a.publishedAt),
		}))
		.filter((group) => group.items.length > 0);

	$: {
		activitiesGroupedByYear = [];
		if (activitiesGroupedByType.length === 1) {
			activitiesGroupedByYear = Array.from(
				new Set(filteredActivities.map((item) => item.dateIssued?.getFullYear())),
			)
				.sort()
				.reverse()
				.map((date) => ({
					value: date,
					label: date,
					items: filteredActivities.filter((item) => item.dateIssued?.getFullYear() === date),
				}));
		}
	}

	const handleFilterChange = (event) => {
		const { id, selectedItems } = event.detail;
		activeFilters = activeFilters
			.filter((item) => item.key !== id)
			.concat(selectedItems.map((item) => ({ ...item, key: id })));
		applyFilters();
	};

	const handleFilterRemove = (event) => {
		const { filter } = event.detail;
		activeFilters = activeFilters
			.filter((item) => item.value !== filter.value || item.key !== filter.key)
			.filter((item, index, array) => {
				if (!array.some((filter) => filter.key === 'type')) {
					return item.key !== 'subtype';
				}
				return true;
			});
		applyFilters();
	};

	const applyFilters = () => {
		// Reset search params
		$page.url.searchParams.forEach((value, key) => {
			$page.url.searchParams.delete(key);
		});

		// Reapply search params
		activeFilters.forEach((item, index) => {
			$page.url.searchParams.append(item.key, item.value);
		});

		goto($page.url, { replaceState: true, keepFocus: true });

		initIsotope();
	};

	async function initIsotope() {
		await tick();
		if (browser) {
			[...activitiesGroupedByType, ...activitiesGroupedByYear].map((group) => {
				if (!document.querySelector(`#year-${group.value} .isotope-grid`)) {
					return;
				}

				const isotopeGrid = new Isotope(`#year-${group.value} .isotope-grid`, {
					itemSelector: '.isotope-grid-item',
					layoutMode: 'masonry',
					percentPosition: true,
					masonry: {
						columnWidth: '.isotope-column-sizer',
					},
				});

				imagesLoaded(`#year-${group.value} .isotope-grid`).on('progress', () => {
					isotopeGrid.layout();
				});
			});
		}
	}

	onMount(async () => {
		Isotope = (await import('isotope-layout')).default;
		imagesLoaded = (await import('imagesloaded')).default;
		initIsotope();
	});
</script>

<div>
	<div class="container">
		<div class="flex items-center justify-between my-10">
			<h1 class="h1">{$LL.activities.title()}</h1>
			<div class="flex space-x-6">
				<Select
					id="type"
					items={filters.type.items}
					label={$LL.filtering.filters.type.label()}
					on:change={handleFilterChange}
					selectedItemValues={activeFilters
						.filter((filter) => filter.key === 'type')
						.map((filter) => filter.value)}
				/>
				{#if activeFilters.filter((filter) => filter.key === 'type').length > 0}
					<Select
						id="subtype"
						items={filters.type.items.find(
							(item) => activeFilters.find((filter) => filter.key === 'type').value === item.value,
						).items}
						label={$LL.filtering.filters.subtype.label()}
						on:change={handleFilterChange}
						selectedItemValues={activeFilters
							.filter((filter) => filter.key === 'subtype')
							.map((filter) => filter.value)}
					/>
				{/if}
				<MultiSelect
					id="date"
					items={filters.date.items}
					label={$LL.filtering.filters.date.label()}
					on:change={handleFilterChange}
					selectedItemValues={activeFilters
						.filter((filter) => filter.key === 'date')
						.map((filter) => filter.value)}
				/>
				<MultiSelect
					id="tags"
					items={filters.tags.items}
					label={$LL.filtering.filters.tag.label()}
					on:change={handleFilterChange}
					selectedItemValues={activeFilters
						.filter((filter) => filter.key === 'tags')
						.map((filter) => filter.value)}
				/>
			</div>
		</div>
		<div class="flex space-x-2 mb-8">
			{#each activeFilters as filter}
				<ActiveFilter {filter} on:remove={handleFilterRemove} />
			{/each}
		</div>
	</div>

	{#each activitiesGroupedByYear.length > 0 ? activitiesGroupedByYear : activitiesGroupedByType as group}
		<section id={`year-${group.value}`}>
			<header>
				<div class="container">
					<h2 class="h5 px-4 pt-1.5 pb-2 rounded-t-xl bg-neutral-200 w-max">{group.label}</h2>
				</div>
			</header>
			<div
				class="relative py-6 after:block after:bg-gradient-to-b after:from-neutral-200 after:to-neutral-200/0 after:h-52 after:absolute after:top-0 after:w-full after:-z-10"
			>
				<div class="container">
					<ul class="isotope-grid">
						<li class="isotope-column-sizer" />
						{#each activitiesGroupedByType.length > 1 ? group.items.slice(0, 5) : group.items as announcement, index}
							<li
								class={clsx('isotope-grid-item p-4', {
									'w-full md:w-1/2 lg:w-1/3 xl:w-1/4': index > 1,
									'w-full lg:w-2/3 xl:w-1/2': index == 1,
									'w-full': index == 0,
								})}
							>
								<AnnouncementCard {announcement} size={index === 0 ? 'large' : 'small'} heading="h3" />
							</li>
						{/each}
					</ul>
					{#if activitiesGroupedByType.length > 1}
						<div class="flex justify-end p-4">

							<Button
							on:click={() =>
								handleFilterChange(
									new CustomEvent('change', {
										detail: {
											id: 'type',
											selectedItems: [
												filters.type.items.find((item) => item.value === group.value),
											],
										},
									}),
									)}
						>
						{$LL.common.seeMore()}
						</Button>
						</div>
					{/if}
				</div>
			</div>
		</section>
	{/each}
</div>
